import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { apiClient } from "@/api/client";
import { cn } from "@/lib/utils";
import { useStorageList } from "@/features/storages/hooks/useStorages";
import { isStorageIntegratedAvailable } from "@/features/storages/storage-ux";
import {
  LayoutDashboard,
  HardDrive,
  Link2,
  FileText,
  History,
  Users,
  Building2,
  Download,
} from "lucide-react";
import { GlobalNavigation } from "./GlobalNavigation";
import { NavigationSidebar } from "./ConsoleShell";
import { CustomerContextMenu } from "./CustomerContextMenu";
import { useSecureAccessSettings } from "@/features/app-bindings/hooks/useAppBindings";
import { resolveClientCertificateExpiryState } from "@/features/app-bindings/secure-access-ux";
import { useKintoneEnvironment } from "@/features/app-bindings/hooks/useKintoneEnvironment";
import { planStatusOf } from "@/features/billing/PlanStatusBanner";

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/console/dashboard", labelKey: "sidebar.dashboard", icon: LayoutDashboard },
  { to: "/console/storages", labelKey: "sidebar.storages", icon: HardDrive },
  { to: "/console/app-bindings", labelKey: "sidebar.appBindings", icon: Link2 },
  { to: "/console/asynchronous-executions", labelKey: "sidebar.asynchronousExecutions", icon: History },
  { to: "/console/audit-log", labelKey: "sidebar.auditLog", icon: FileText },
  { to: "/console/downloads", labelKey: "sidebar.downloads", icon: Download },
  { to: "/admin/users", labelKey: "sidebar.users", icon: Users, adminOnly: true },
  { to: "/console/service-subscription", labelKey: "sidebar.serviceSubscription", icon: Building2, adminOnly: true },
];

const ONBOARDING_CHIP_CLASS =
  "inline-flex w-fit rounded-full bg-amber-300 px-2 py-0.5 text-[11px] font-semibold leading-none text-amber-950 shadow-sm ring-1 ring-amber-500/50";

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.currentMembership?.role === "customer_admin";
  const hasRegisteredKintoneEnvironment = Boolean(
    user?.serviceSubscription?.kintoneSubdomain,
  );
  const { view: secureAccessView } = useSecureAccessSettings({
    enabled: isAdmin && hasRegisteredKintoneEnvironment,
  });
  const certificateExpiryState = resolveClientCertificateExpiryState(
    secureAccessView?.connectionKind === "secure_access",
    secureAccessView?.expiresAt ?? null,
  );
  const { environment } = useKintoneEnvironment();
  const planStatus = environment ? planStatusOf(environment.commercial) : null;
  const [shouldPromptAppBinding, setShouldPromptAppBinding] = useState(false);
  const isStorageRoute = location.pathname.startsWith("/console/storages");
  const isAppBindingRoute = location.pathname.startsWith("/console/app-bindings");
  const {
    items: storages,
    isLoading: isLoadingStorages,
    error: storageListError,
    refresh: refreshStorages,
  } = useStorageList({ limit: "100" });
  const storageListContext = `${user?.currentMembership?.customerId ?? ""}:${location.key}`;
  const previousStorageListContext = useRef(storageListContext);

  useEffect(() => {
    if (previousStorageListContext.current === storageListContext) return;
    previousStorageListContext.current = storageListContext;
    // The sidebar survives navigation, unlike the page's storage list.
    // Reload configuration and membership, not only polled runtime status.
    refreshStorages();
  }, [storageListContext, refreshStorages]);

  const hasNoStorages =
    !isLoadingStorages && !storageListError && storages.length === 0;
  const hasOnboardingReadyStorage = storages.some(
    isStorageIntegratedAvailable,
  );
  const hasPotentiallyAvailableStorageWithPendingRuntimeStatus = storages.some(
    (storage) =>
      storage.storageLifecycleState === "active" &&
      storage.settingState === "ready" &&
      !storage.runtimeStatusSnapshot,
  );
  const shouldPromptStorageReadiness =
    !isLoadingStorages &&
    !storageListError &&
    storages.length > 0 &&
    !hasOnboardingReadyStorage &&
    !hasPotentiallyAvailableStorageWithPendingRuntimeStatus;

  useEffect(() => {
    let cancelled = false;
    setShouldPromptAppBinding(false);

    if (
      isLoadingStorages ||
      storageListError ||
      storages.length === 0 ||
      !hasOnboardingReadyStorage
    ) {
      return () => {
        cancelled = true;
      };
    }

    apiClient<{ items: unknown[] }>("/kintone-apps?limit=1")
      .then((appBindingsResult) => {
        if (!cancelled) {
          setShouldPromptAppBinding(appBindingsResult.items.length === 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShouldPromptAppBinding(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    hasOnboardingReadyStorage,
    hasPotentiallyAvailableStorageWithPendingRuntimeStatus,
    isLoadingStorages,
    storageListError,
    storages.length,
    location.pathname,
    user?.currentMembership?.customerId,
  ]);

  return (
    <NavigationSidebar>
      <CustomerContextMenu />

      <nav aria-label="組織内メニュー" className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 py-3">
        {NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex items-start gap-3 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "border-primary bg-accent text-accent-foreground"
                  : "border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )
            }
          >
            <item.icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="sidebar-label min-w-0 flex-1 space-y-1">
              <span className="block truncate">{t(item.labelKey)}</span>
              {item.to === "/console/storages" &&
              (hasNoStorages || shouldPromptStorageReadiness) &&
              !isStorageRoute ? (
                <span className={ONBOARDING_CHIP_CLASS}>
                  {t(
                    hasNoStorages
                      ? "sidebar.storageOnboardingChip"
                      : "sidebar.storageReadinessChip",
                  )}
                </span>
              ) : null}
              {item.to === "/console/app-bindings" &&
              shouldPromptAppBinding &&
              !isAppBindingRoute ? (
                <span className={ONBOARDING_CHIP_CLASS}>
                  {t("sidebar.appBindingOnboardingChip")}
                </span>
              ) : null}
              {item.to === "/console/service-subscription" && planStatus?.tone === "stopped" ? (
                <span className="inline-flex w-fit rounded-full bg-red-200 px-2 py-0.5 text-[11px] font-semibold leading-none text-red-900 ring-1 ring-red-400/60">
                  要対応
                </span>
              ) : item.to === "/console/service-subscription" && planStatus?.tone === "attention" ? (
                <span className={ONBOARDING_CHIP_CLASS}>
                  お試し残り{environment?.commercial.trialDaysRemaining}日
                </span>
              ) : item.to === "/console/service-subscription" &&
               secureAccessView?.connectionKind === "secure_access" &&
               certificateExpiryState === "expired" ? (
                <span className="inline-flex w-fit rounded-full bg-red-200 px-2 py-0.5 text-[11px] font-semibold leading-none text-red-900 ring-1 ring-red-400/60">
                  証明書期限切れ
                </span>
              ) : item.to === "/console/service-subscription" &&
                  secureAccessView?.connectionKind === "secure_access" &&
                  certificateExpiryState === "expiring_soon" ? (
                <span className={ONBOARDING_CHIP_CLASS}>証明書更新</span>
              ) : null}
            </span>
          </NavLink>
        ))}
      </nav>
      <GlobalNavigation />
    </NavigationSidebar>
  );
}
